import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Image, Platform, Pressable, Text, View } from 'react-native';
import type { Business } from '@/types/business';

interface BusinessLocationSectionProps {
  business: Business;
  onMapsPress: () => void;
}

export function BusinessLocationSection({ business, onMapsPress }: BusinessLocationSectionProps) {
  const hasAddress = business.street || business.city || business.state || business.postalCode;
  const hasCoords = business.latitude && business.longitude;

  if (!hasAddress && !hasCoords) return null;

  return (
    <View>
      <Text className="text-2xl font-semibold mb-3 text-foreground" accessibilityRole="header">
        Location
      </Text>

      <View className="mb-4 rounded-lg overflow-hidden bg-surface">
        {hasCoords && (
          <View className="bg-border relative overflow-hidden" style={{ height: 200 }}>
            {Platform.OS === 'web' ? (
              <iframe
                width="100%"
                height="200"
                style={{ border: 0, margin: 0, padding: 0, pointerEvents: 'none' }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${business.longitude! - 0.01},${business.latitude! - 0.01},${business.longitude! + 0.01},${business.latitude! + 0.01}&layer=mapnik&marker=${business.latitude},${business.longitude}`}
              />
            ) : (
              <>
                <Image
                  source={{
                    uri: `https://staticmap.openstreetmap.de/staticmap.php?center=${business.latitude},${business.longitude}&zoom=15&size=800x400&markers=${business.latitude},${business.longitude},red-pushpin`
                  }}
                  style={{ width: '100%', height: 200 }}
                  resizeMode="cover"
                  accessibilityLabel={`Map showing location of ${business.name}`}
                />
                <View style={{ position: 'absolute', top: '50%', left: '50%', marginLeft: -12, marginTop: -24 }}>
                  <Ionicons name="location" size={24} className="text-primary" />
                </View>
              </>
            )}
          </View>
        )}

        <View className="p-4">
          <View className="flex-row items-start mb-3">
            <Ionicons name="location-outline" size={20} className="text-muted-foreground" style={{ marginTop: 2, marginRight: 12 }} />
            <View className="flex-1">
              {business.street && (
                <Text className="text-base mb-1 text-foreground">
                  {business.street}
                </Text>
              )}
              {(business.city || business.state || business.postalCode) && (
                <Text className="text-sm text-muted-foreground">
                  {[business.city, business.state, business.postalCode].filter(Boolean).join(', ')}
                </Text>
              )}
            </View>
          </View>

          {hasCoords && (
            <Pressable
              onPress={onMapsPress}
              className="flex-row items-center justify-center p-3 rounded-lg mt-2 bg-surface border border-white"
            >
              <Ionicons name="map-outline" size={18} className="text-foreground" style={{ marginRight: 8 }} />
              <Text className="font-semibold text-foreground">
                Open in Maps
              </Text>
            </Pressable>
          )}
        </View>
      </View>
    </View>
  );
}
